import PropTypes from 'prop-types'; // ✅ Import prop-types
import { Modal, Form, Input, Button, message, Typography } from 'antd';
import { useChangePassword } from '../../Hooks/Index';

const { Text } = Typography;

export default function ChangePasswordModal({ isOpen, onLogout }) {
  const [form] = Form.useForm();

  const changePasswordMutation = useChangePassword({
    onSuccess: () => {
      message.success('Password changed successfully. Logging out...');
      setTimeout(() => {
        onLogout();
      }, 1500);
    },
    onError: (error) => {
      message.error(
        error?.response?.data?.message || 'Failed to change password'
      );
    },
  });

  const handleSubmit = (values) => {
    changePasswordMutation.mutate({
      oldPassword: values.oldPassword,
      newPassword: values.newPassword,
      comparePassword: values.comparePassword,
    });
  };

  return (
    <Modal
      destroyOnHidden
      closable={false} // ✅ Modal cannot be closed until password is changed
      footer={null}
      maskClosable={false}
      open={isOpen}
      title="Change Your Password"
    >
      <Text type="secondary">
        Your current password has expired or needs to be changed for security
        reasons. Please enter a strong new password to continue.
      </Text>
      <div className="mt-4">
        <Form
          className="mt-4"
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {/* Old Password */}
          <Form.Item
            label="Old Password"
            name="oldPassword"
            rules={[
              { required: true, message: 'Please enter your current password' },
            ]}
          >
            <Input.Password placeholder="Enter old password" />
          </Form.Item>

          {/* New Password */}
          <Form.Item
            hasFeedback
            label="New Password"
            name="newPassword"
            rules={[
              { required: true, message: 'Please enter a new password' },
              {
                min: 8,
                message: 'Password must be at least 8 characters long',
              },
              {
                pattern: /[A-Z]/,
                message: 'Password must contain at least one uppercase letter',
              },
              {
                pattern: /[a-z]/,
                message: 'Password must contain at least one lowercase letter',
              },
              {
                pattern: /\d/,
                message: 'Password must contain at least one number',
              },
              {
                pattern: /[@$!%*?&]/,
                message:
                  'Password must contain at least one special character (@$!%*?&)',
              },
            ]}
          >
            <Input.Password placeholder="Enter new password" />
          </Form.Item>

          {/* Confirm New Password */}
          <Form.Item
            hasFeedback
            dependencies={['newPassword']}
            label="Confirm New Password"
            name="comparePassword"
            rules={[
              { required: true, message: 'Please confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('New password and confirm password must match')
                  );
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm new password" />
          </Form.Item>

          <div className="flex justify-end space-x-2">
            <Button
              htmlType="submit"
              loading={changePasswordMutation.isPending}
              type="primary"
            >
              Change Password
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
}

// ✅ Add Prop Validation
ChangePasswordModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onLogout: PropTypes.func.isRequired,
};
